import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'

import { HeroNebula } from './hero-nebula'

/**
 * The dark hero every v2 page opens on: eyebrow, Anton display title,
 * subtitle, an action row, and an optional media slot. Renders its own
 * .cw-dark full-bleed section; callers do not wrap it in another section.
 *
 * Two optional lower-half slots (Peter's homepage direction, 2026-08-10):
 * `mediaBelow` drops the media out of the side-by-side row into a
 * full-width panel under the copy, and `stats` adds a hairline-separated
 * stat strip across the bottom edge of the hero. Per spec section 5.1
 * (never plausible-looking invented numbers), callers must pass verified
 * figures.
 *
 * Two of the homepage stats this comment previously vouched for were wrong
 * and were replaced on 2026-09-06. "3M certified installers" contradicted
 * src/components/content/trust-signals.tsx, which states we hold no
 * manufacturer installer certification; it is now WCB-covered installers.
 * "5-year install warranty" conflated the film manufacturer's product
 * warranty with our own workmanship warranty, which src/content/pages/
 * warranty.mdx puts at 14 days; the stat now reads as the manufacturer's
 * 3/5/10 year product warranty and links to that page.
 *
 * The years figure said 14 while src/components/home/trust-bar.tsx, which
 * renders directly below this hero, said 10+. Both were on screen at once.
 * Peter confirmed 10+ is correct (2026-09-06) and the hero now matches; keep
 * the two in step if either changes.
 *
 * Still unverified: 1,200+ vehicles wrapped, and the TrustBar's 200+ fleet
 * jobs and 3,000 sq ft facility. The earlier "confirmed 2026-08-10" note
 * pointed at no underlying source. Establish provenance before treating any
 * of them as verified.
 */
export function V2Hero({
  eyebrow,
  title,
  subtitle,
  actions,
  media,
  mediaBelow,
  stats,
  className,
  dark = true,
}: {
  eyebrow?: ReactNode
  title: ReactNode
  subtitle?: ReactNode
  actions?: ReactNode
  media?: ReactNode
  mediaBelow?: boolean
  stats?: Array<{ value: ReactNode; label: ReactNode }>
  className?: string
  /** Set false for a light (paper) hero, per the homepage's 2026-09-08
   *  brand-recolour handoff: "The hero is light, not dark." Every other v2
   *  page keeps the dark treatment (default true); the nebula backdrop is
   *  dark-only and is skipped when light. */
  dark?: boolean
}) {
  const sideBySide = Boolean(media) && !mediaBelow

  return (
    <section className={cn(dark && 'cw-dark', 'relative isolate', className)}>
      {/* Background layer only; must stay behind every copy/media node below. */}
      {dark && <HeroNebula />}

      <div
        className={cn(
          'relative z-10 mx-auto max-w-[1200px] px-6',
          // The stat strip owns the bottom edge when it is present, so the
          // container keeps the section's top rhythm only. .cw-section is a
          // descendant rule and outranks a Tailwind pb-0, hence the swap
          // rather than an override.
          stats ? 'pt-[clamp(72px,10vw,140px)] pb-16' : 'cw-section'
        )}
      >
        <div
          className={cn(
            'flex flex-col gap-6',
            sideBySide && 'lg:flex-row lg:items-center lg:justify-between lg:gap-12'
          )}
        >
          <div className='flex flex-col gap-6'>
            {eyebrow && <p className='cw-eyebrow'>{eyebrow}</p>}
            <h1 className='cw-display-1'>{title}</h1>
            {subtitle && <p className='max-w-xl text-lg text-muted'>{subtitle}</p>}
            {actions && <div className='flex flex-wrap gap-4'>{actions}</div>}
          </div>
          {media && <div className={cn(sideBySide ? 'flex-1' : 'mt-10 w-full')}>{media}</div>}
        </div>
      </div>

      {stats && (
        <div className='relative z-10 border-t border-[var(--border)]'>
          <div className='mx-auto grid max-w-[1200px] grid-cols-2 gap-8 px-6 py-12 sm:grid-cols-4'>
            {stats.map((stat, index) => (
              <div key={index} className='flex flex-col gap-2'>
                <h3 className='cw-display-2'>{stat.value}</h3>
                <p className='cw-eyebrow'>{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  )
}
